import {
  type UseMutateAsyncFunction,
  useMutation,
  type UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';

import { queryKeysSite } from './queryKeys';

type SiteData = Record<string, unknown>;

type MutationSiteArguments = {
  siteId: UUID;
  siteData: SiteData;
};

type UpdateSiteResponse = {
  code: string;
  message: string;
  requestId: UUID | undefined;
};

type UpdateSiteMutationOptions = UseMutationOptions<
  UpdateSiteResponse,
  Error,
  MutationSiteArguments
>;

export type UpdateSiteMutateAsyncFunction = UseMutateAsyncFunction<
  UpdateSiteResponse,
  Error,
  MutationSiteArguments
>;

/**
 * Updates a given site.
 *
 * @param {string} siteId - The ID of the site to update.
 * @param {Object} siteData - An object containing the site data to update.
 * @returns {Promise} A promise that resolves with the API response.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Site/operation/update_site_site__site_uuid__put
 */

export const updateSite = async (siteId: UUID, siteData: SiteData) => {
  const response = await vestigasApi
    .put(ENDPOINT.SITE.UPDATE(siteId), {
      json: siteData,
    })
    .json<UpdateSiteResponse>();

  return response;
};

/**
 * Custom hook for updating a given site.
 *
 * @returns {Object} An object containing the mutation function and related properties.
 */
export const useMutationUpdateSite = (options?: UpdateSiteMutationOptions) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ siteId, siteData }: MutationSiteArguments) =>
      updateSite(siteId, siteData),
    onSuccess(_, { siteId }) {
      queryClient.invalidateQueries(queryKeysSite.get(siteId));
    },
    ...options,
  });
};
